import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { CustomToastrService } from '../../../Shared/Services/custom-toastr.service';
import { UserType } from '../../../Shared/Enums/UserType';
import { UserInfoService } from '../../../Services/user-info.service';
import { AuthService } from '../../../Shared/Services/auth.service';
import { DialogService } from '../../../Shared/Services/dialog.service';
import { SharedService } from '../../../Shared/Services/shared.service';
import { LoginVm } from '../../../Model/LoginVM';
import { DtoUserVM } from '../../../Model/Users/DtoUserVM';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrl: './add-user.component.scss'
})
export class AddUserComponent {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  hidePassword = true;
  form!: FormGroup;
  ImgPath?: string;
  isEdit?: boolean = false;
  fileUpload: any;//FileList
  currentUser: LoginVm = {};
  agentId?: number;

  constructor(private api: UserInfoService, private toastrService: CustomToastrService,
    private router: Router, private spinner: NgxSpinnerService, private auth: AuthService, private shared: SharedService,
    private route: ActivatedRoute, private dialogService: DialogService) { }

  ngOnInit() {
    if (this.auth.isAuthenticated([UserType.admin, UserType.Agent])) {
      this.initForm();
      this.GetCurrentUser();
      this.isEdit = false;
      this.route.params.subscribe(params => {
        const id = params['id'];
        if (id) {
          this.isEdit = true;
          this.GetUserData(id);
        }
      });

      this.route.queryParams.subscribe(params => {
        const agentId = params['agentId'];
        this.agentId = agentId;
      });
    } else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  GetCurrentUser() {
    let jsonObj = this.auth.getStorage("currentUser");
    this.currentUser = <LoginVm>JSON.parse(jsonObj);
  }

  get f() {
    return this.form.controls;
  }

  initForm() {
    this.form = new FormGroup({
      id: new FormControl('', []),
      phoneNumber: new FormControl('', [Validators.required, Validators.minLength(11), Validators.pattern(/^-?([0\u0660][0-9\u0660-\u0669]*)?$/)]),
      userName: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.email])
    });
  }

  submit() {
    if (this.form.valid) {
      this.dialogService.confirmDialog({
        titleKey: 'confirmation',
        messageKey: 'AreYouSuretoSaveThis',
        confirmTextKey: 'Yes',
        cancelTextKey: 'No'
      }).subscribe(isConfirmed => {
        if (isConfirmed) {
          this.CreateNewUser(this.form.value);
        }
      });
    }
    else {
      this.shared.FireFormValidation(this.form);
    }
  }

  CreateNewUser(form: DtoUserVM) {
    this.spinner.show();
    form.userType = this.currentUser.userType?.toString();
    form.agentId = this.agentId;

    if (form.id) {
      this.EditUser(form);
    }
    else {
      this.AddUser(form);
    }
  }

  EditUser(form: DtoUserVM) {
    this.api.EditUser(form).pipe(takeUntil(this.destroy$))
      .subscribe(
        {
          next: (data: any) => {
            this.spinner.hide();
            if (data.isSuccess) {
              this.toastrService.showSuccessToast('Success', 'EditedSuccessfully');
              setTimeout(() => {
                this.router.navigate(
                  ['dashboard/users'],
                  { queryParams: { agentId: this.agentId } }
                );
              }, 600);
            } else {
              this.toastrService.showErrorToast('Error', data.message);
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
  }

  AddUser(form: any) {
    this.api.CreateNewUser(form).pipe(takeUntil(this.destroy$))
      .subscribe(
        {
          next: (data: any) => {
            this.spinner.hide();
            if (data.isSuccess) {
              this.toastrService.showSuccessToast('Success', 'AddedSuccessfully');
              setTimeout(() => {
                this.router.navigate(
                  ['dashboard/users'],
                  { queryParams: { agentId: this.agentId } }
                );
              }, 600);
            } else {
              this.toastrService.showErrorToast('Error', data.message);
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
  }

  Cancel() {
    if (this.form.touched) {
      this.dialogService.confirmDialog({
        titleKey: 'confirmation',
        messageKey: 'AreYouSuretoBackWithoutSave',
        confirmTextKey: 'Yes',
        cancelTextKey: 'No'
      }).subscribe(isConfirmed => {
        if (isConfirmed) {
          this.router.navigate(
            ['dashboard/users'],
            { queryParams: { agentId: this.agentId } }
          );
        }
      });
    }
    else {
      this.router.navigate(
        ['dashboard/users'],
        { queryParams: { agentId: this.agentId } }
      );
    }
  }

  GetUserData(userId: any) {
    if (this.auth.isAuthenticated([UserType.admin, UserType.Agent])) {
      this.api.GetUserData(userId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: any) => {
            this.spinner.hide();
            if (data.isSuccess) {
              this.setFormValue(data.payload);
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
    }
    else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  setFormValue(data: any) {
    this.form.setValue({
      id: data.id,
      phoneNumber: data.phoneNumber,
      userName: data.userName,
      email: data.email,
      password: ''
    });
  }
}
